import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {LinearProgress, TablePagination} from "@mui/material";
import {useSitStores} from "~/stores";
import {INITIATIVE_COLUMNS} from "./columns";
import {MithraTablePaginationActions} from "~/components/table-pagination/MithraTablePaginationActions.tsx";
import {generatePath, useNavigate} from "react-router";
import {sitRoutes} from "../../routing/sitRoutes";
import {InitiativeFilters} from "./InitiativeFilters";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent.tsx";
import moment from "moment";
import {Link as LinkIcon} from "@mui/icons-material";
import {SitDoiPill, SitFarolPill, SitTrafficDot} from "../../components/design/SitDesignAtoms";
import {CategorizationViewOnlyComponent} from "~/components/categorization/CategorizationViewOnlyComponent";

const RIGHT_COLS = ['total_saving', 'total_avoidance', 'updated'];

export const InitiativeTable: React.FC = observer(() => {
    const {ccStore, initiativeListStore: store} = useSitStores();
    const navigate = useNavigate();

    useEffect(() => {
        store.reloadData(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const open = (id: number) => navigate(generatePath(sitRoutes.sit_initiative, {initiativeId: id}));
    const sortOn = (field: string) => {
        if (field) store.setOrdering(store.ordering === field ? '-' + field : field);
    };
    const caret = (field: string) => store.ordering === field ? ' ▲' : store.ordering === '-' + field ? ' ▼' : '';
    const result = store.initiativeListRM.result;

    return (
        <div className="initiative-table-component sit-list">
            <InitiativeFilters/>

            {store.initiativeListRM.busy && <LinearProgress variant="indeterminate"/>}

            <div className="sit-table-wrap">
                <table className="sit-table">
                    <thead>
                        <tr>
                            {INITIATIVE_COLUMNS.map((c, i) => (
                                <th key={i}
                                    className={(RIGHT_COLS.includes(c.columnFieldId ?? '') ? 'right ' : '')
                                    + (c.columnFieldId ? 'sortable' : '')
                                    + (store.ordering.replace('-', '') === c.columnFieldId ? ' sorted' : '')}
                                    style={{minWidth: c.minWidth}}
                                    onClick={() => sortOn(c.columnFieldId ?? '')}>
                                    {c.txt}<span className="sit-sort-caret">{caret(c.columnFieldId ?? '')}</span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    {result && <tbody>
                        {result.results.map((row) => (
                            <tr key={row.id} onClick={() => open(row.id)} style={{cursor: 'pointer'}}>
                                <td>
                                    <div className="sit-row-title">
                                        {row.title}
                                        {row.evidence && row.evidence.length > 0 &&
                                        <a className="sit-evid" href={row.evidence[0].url} target="_blank"
                                           rel="noreferrer" title={row.evidence[0].label || row.evidence[0].url}
                                           onClick={e => e.stopPropagation()}>
                                            <LinkIcon fontSize="inherit"/>
                                        </a>}
                                    </div>
                                    {ccStore.showCustomerId && <div className="sit-row-id">{row.customer_id}</div>}
                                </td>
                                <td>{row.owner ? row.owner.name : 'No owner'}</td>
                                <td>{row.financial.map(f => f.companies.map(c => ccStore.getCompanyTitle(c)).join(', ')).join(', ')}</td>
                                <td>
                                    {ccStore.showFullCategory
                                        ? <CategorizationViewOnlyComponent
                                            category={ccStore.getCategoryLabels(row.financial?.at(0)?.categories?.at(0) ?? 0)}/>
                                        : ccStore.getCategoryL1(row.financial?.at(0)?.categories?.at(0) ?? 0)}
                                </td>
                                <td>{ccStore.getLeverGroupTitle(row.lever_id)}</td>
                                <td><SitTrafficDot initiative={row} withLabel/></td>
                                {/* Approval applies at the last stage (EBIT effective): approved, or pending until approved. */}
                                <td><SitDoiPill doi={row.doi_status}
                                                approvalState={row.approved ? 'approved' : row.doi_status === 5 ? 'pending' : null}/></td>
                                <td><SitFarolPill tier={row.complexity_tier} sm/></td>
                                <td className="right">
                                    <CurrencyComponent nDigits={3}
                                                       v={row.financial.reduce((sum, f) => sum + parseFloat(String(f.saving_amount)), 0) || 0}/>
                                </td>
                                <td className="right">
                                    <CurrencyComponent nDigits={3}
                                                       v={row.financial?.reduce((sum, f) => sum + parseFloat(String(f.avoidance_amount)), 0) || 0}/>
                                </td>
                                <td className="right">{row.updated ? moment(row.updated).format('L') : ''}</td>
                                <td>{row.responsible?.name}</td>
                            </tr>
                        ))}
                    </tbody>}
                </table>
            </div>

            <TablePagination
                component="div"
                count={result?.count ?? 0}
                rowsPerPage={store.per_page}
                page={store.page}
                onPageChange={(_e, page: number) => store.setPage(page)}
                onRowsPerPageChange={(event) => store.setPerPage(parseInt(event.target.value) || 0)}
                ActionsComponent={MithraTablePaginationActions}
            />
        </div>
    );
});
